/**
 * 사전 차단 — **동의 전에는 제3자로 아무것도 내보내지 않는다.**
 *
 * ## 기준이 쿠키가 아니다
 *
 * 「쿠키를 심는가」가 아니라 **「동의 전에 제3자로 데이터가 나가는가」**가
 * 기준이다. 폰트 CDN 은 쿠키를 심지 않아도 **방문자 IP 를 넘긴다.** 독일
 * 법원이 2022년에 동의 없는 Google Fonts 임베드를 GDPR 위반으로 판단한
 * 선례가 있다.
 *
 * 실측에서 두 임베드처 모두 **동의 전 쿠키가 0개**였는데 **차단 대상은
 * 있었다** — `docs/discovery/2026-10-07_current-banner.md` §2-1.
 *
 * ## 🔴 두 경로를 나눠서 봐야 한다 — 실측으로 확인했다
 *
 * | 어떻게 들어오나 | 막히나 | 어떻게 |
 * |---|:--:|---|
 * | **동적 주입** (`appendChild` 등) | ✅ | 이 모듈이 자동으로 잡는다 |
 * | **파서 삽입** (`<head>` 의 태그) | ⚠️ | **사이트가 표식을 달아야 한다** |
 *
 * 파서가 삽입하는 요소는 **`appendChild` 를 거치지 않는다.** DOM 트리에
 * 직접 들어가므로 가로채기가 보지 못한다. 그리고 이 코드가 도는 시점에는
 * 뒤쪽 태그가 **아직 파싱되지도 않아** `querySelectorAll` 로도 안 잡힌다.
 *
 * **고전 스크립트(IIFE)를 `<head>` 맨 앞에 둬도 마찬가지다.** 실측으로
 * 확인했다 — 순서를 그렇게 해도 뒤의 `<link>`·`<script>` 가 그대로 나갔다.
 *
 * ### 그래서 파서 삽입 태그는 **표식을 단다**
 *
 * ```html
 * <script type="text/plain" data-ray-src="https://cdn.example.com/x.js"></script>
 * <link rel="stylesheet" data-ray-href="https://fonts.googleapis.com/css2?...">
 * ```
 *
 * `type="text/plain"` 이면 브라우저가 **실행하지 않고 요청도 보내지 않는다.**
 * 동의 후 `release()` 가 진짜 태그로 바꿔 단다.
 *
 * 현행 CMP 도 같은 방식을 요구한다(`_iub_cs_activate`). 그리고 **두 임베드처
 * 모두 그 표식이 0건**이었다 — 지금 사전 차단이 실제로는 안 걸려 있다는
 * 뜻이고, 이 조사 결과와 맞아떨어진다.
 *
 * **이 작업은 임베드처마다 반복된다.** 차터가 사전 차단을 따로 떼어 적고
 * 「연동 사이트 수만큼 반복된다」고 한 이유가 이것이다.
 *
 * ## 가장 좋은 차단은 차단하지 않는 것이다
 *
 * **폰트는 자체 호스팅으로 옮기면 대상에서 사라진다.** 가로채는 것보다
 * 간단하고, 깨질 구석이 없고, 동의 여부와 무관하게 글꼴이 제대로 나온다.
 * 차단은 **옮길 수 없는 것**(오류 추적·영상 임베드 등)에만 쓴다.
 */
import type { BannerConfig } from './types.js';

/** 막아 둔 요소를 되살리기 위해 기억해 둔다. */
interface Held {
  readonly el: Element;
  readonly attr: string;
  readonly url: string;
}

/** 목적 id → 그 목적의 호스트 목록. */
export type HostMap = Readonly<Record<string, readonly string[]>>;

/** 설정에서 목적별 호스트 지도를 만든다. */
export function hostMap(config: BannerConfig): HostMap {
  const m: Record<string, readonly string[]> = {};
  for (const p of config.purposes) {
    if (p.hosts !== undefined && p.hosts.length > 0) m[p.id] = p.hosts;
  }
  return m;
}

/** 막아야 할 호스트 전체 — 목적에 묶인 것 + 묶이지 않은 것. */
export function allBlockHosts(config: BannerConfig): readonly string[] {
  return [
    ...config.purposes.flatMap((p) => p.hosts ?? []),
    ...(config.blockHostsUnassigned ?? []),
  ];
}

const held: Held[] = [];

function hostOf(url: string): string | null {
  // **`location` 에 기대지 않는다.** 브라우저 밖(테스트·빌드 도구)에서는
  // 없고, 있다고 가정하면 거기서 모든 판정이 「차단 안 함」으로 떨어진다.
  // 조용히 뚫리는 종류라 테스트가 먼저 잡았다.
  try {
    return new URL(url).hostname;
  } catch {
    /* 절대 URL 이 아니다 — 아래에서 상대 경로로 다시 본다 */
  }
  const base =
    typeof location !== 'undefined' && typeof location.href === 'string'
      ? location.href
      : null;
  if (base === null) {
    // 기준이 없으면 상대 경로는 **같은 출처**로 본다. 제3자일 수 없다.
    return null;
  }
  try {
    return new URL(url, base).hostname;
  } catch {
    return null;
  }
}

/** 그 URL 이 차단 대상인가. 호스트 **접미사**로 본다. */
export function isBlocked(url: string, blockHosts: readonly string[]): boolean {
  const h = hostOf(url);
  if (h === null) return false;
  return blockHosts.some((b) => h === b || h.endsWith(`.${b}`));
}

/**
 * **표식이 달린 요소**를 찾아 보류 목록에 넣는다.
 *
 * `data-ray-src`/`data-ray-href` 가 있으면 그 값을 기억해 둔다. 태그 자체는
 * 이미 `type="text/plain"` 등으로 **요청을 보내지 않는 상태**다 — 우리가
 * 막는 것이 아니라 **사이트가 애초에 안 나가게 써 둔 것**이고, 우리는
 * 동의 후 되살리는 역할만 한다.
 *
 * 차단 대상 목록에 없어도 표식이 있으면 보류한다. 표식을 단 것 자체가
 * 「동의 전에는 내보내지 마라」는 선언이다.
 */
export function holdMarked(): number {
  let n = 0;
  for (const [attr, real] of [
    ['data-ray-src', 'src'],
    ['data-ray-href', 'href'],
  ] as const) {
    for (const el of Array.from(document.querySelectorAll(`[${attr}]`))) {
      const url = el.getAttribute(attr);
      if (url === null || url === '') continue;
      // 두 번 담지 않는다 — 아래 `watchMarked` 가 여러 번 부른다.
      if (held.some((h) => h.el === el)) continue;
      held.push({ el, attr: real, url });
      n++;
    }
  }
  return n;
}

/**
 * 표식 태그를 **파싱이 끝날 때까지 계속 주워 담는다.**
 *
 * 🔴 `<head>` 맨 앞에서 돌면 **뒤쪽 태그가 아직 파싱되지 않았다.** 그때
 * 한 번만 훑으면 아무것도 못 찾고, 동의해도 되살릴 것이 없어 **조용히
 * 아무 일도 일어나지 않는다.** 실제로 그렇게 깨졌다.
 *
 * 표식 태그는 **애초에 요청을 보내지 않으므로** 급할 것이 없다. 늦게
 * 주워도 된다. 급한 것은 차단이지 수집이 아니다.
 */
export function watchMarked(): void {
  holdMarked();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => void holdMarked(), { once: true });
  }
  // 파싱 뒤에 표식 태그를 심는 경우까지 본다. 요청을 안 보내는 태그라
  // 늦게 발견해도 늦은 것이 아니다.
  window.addEventListener('load', () => void holdMarked(), { once: true });
}

/**
 * 이미 문서에 있는 요소 중 대상을 **보류**한다.
 *
 * `src`/`href` 를 떼어 `data-ray-blocked` 로 옮긴다. 떼어 두면 브라우저가
 * 요청을 보내지 않는다.
 *
 * ⚠️ **이 함수가 늦게 돌면 소용없다.** 브라우저는 태그를 만나는 즉시
 * 요청을 보내므로, **문서 `<head>` 의 가장 앞**에서 불러야 한다.
 * 번들러가 스크립트를 아래로 옮기면 차단이 뚫린다.
 */
export function holdExisting(blockHosts: readonly string[]): number {
  let n = 0;
  for (const [sel, attr] of [
    ['script[src]', 'src'],
    ['link[rel="stylesheet"][href]', 'href'],
    ['iframe[src]', 'src'],
    ['img[src]', 'src'],
  ] as const) {
    for (const el of Array.from(document.querySelectorAll(sel))) {
      const url = el.getAttribute(attr);
      if (url === null || !isBlocked(url, blockHosts)) continue;
      el.setAttribute('data-ray-blocked', url);
      el.removeAttribute(attr);
      held.push({ el, attr, url });
      n++;
    }
  }
  return n;
}

/**
 * **앞으로 추가되는** 요소를 가로챈다.
 *
 * 대부분의 추적 코드는 `createElement('script')` 로 태그를 만들어 붙인다.
 * 이미 있는 것만 막으면 그게 그대로 통과한다.
 *
 * `MutationObserver` 로는 늦다 — 콜백이 돌 때 요청은 이미 나갔다. 그래서
 * **`appendChild`·`insertBefore` 를 감싼다.** 붙는 순간 속성을 떼어 낸다.
 */
/**
 * **우리가 설치한 패치**를 기억해 둔다.
 *
 * 🔴 「이미 설치했나」를 **실제로 설치한 참조와 비교**해야 한다. 가드가
 * 비교하는 것과 올리는 것이 다르면 가드는 늘 통과하고, 부를 때마다 한 겹씩
 * 쌓인다. 그러면 되돌리기가 바깥 한 겹만 벗겨 **원본이 영영 안 돌아온다.**
 *
 * 소비 제품에서 같은 모양의 결함이 실증됐다 — 구독 3회 뒤 훅 1회 호출에
 * 리스너가 3번 불렸다.
 */
const installed = new Map<string, unknown>();

export function interceptFuture(blockHosts: readonly string[]): () => void {
  const originals = new Map<string, unknown>();

  for (const name of ['appendChild', 'insertBefore'] as const) {
    const current = Node.prototype[name] as unknown;
    // 지금 올라가 있는 것이 **우리가 올린 바로 그것**이면 다시 올리지 않는다.
    if (installed.get(name) === current) continue;
    const original = current as (...a: unknown[]) => unknown;
    originals.set(name, original);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (Node.prototype as any)[name] = function patched(this: Node, ...args: unknown[]) {
      const node = args[0];
      if (node instanceof Element) {
        for (const attr of ['src', 'href'] as const) {
          const url = node.getAttribute(attr);
          if (url !== null && isBlocked(url, blockHosts)) {
            node.setAttribute('data-ray-blocked', url);
            node.removeAttribute(attr);
            held.push({ el: node, attr, url });
          }
        }
      }
      return original.apply(this, args);
    };
    // 방금 올린 **그 함수**를 기억한다. 가드가 이것과 비교한다.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    installed.set(name, (Node.prototype as any)[name]);
  }

  return () => {
    for (const [name, fn] of originals) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (Node.prototype as any)[name] = fn;
      installed.delete(name);
    }
  };
}

/**
 * 동의한 뒤 **보류한 것을 되살린다.**
 *
 * 스크립트는 속성을 되돌려도 실행되지 않는다 — 브라우저가 이미 「처리한
 * 노드」로 표시해 두기 때문이다. **같은 내용으로 새 요소를 만들어
 * 바꿔치기해야** 실행된다. 여기서 틀리면 동의했는데 아무것도 안 돌아간다.
 */
export function release(allowedHosts: readonly string[] | null = null): number {
  let n = 0;
  const keep: Held[] = [];
  for (const h of held.splice(0)) {
    // `null` 이면 전부 되살린다 (규제가 사전 동의를 요구하지 않는 경우).
    // 목록이 있으면 **그 목록에 속한 것만** 푼다 — 거부한 목적의 자원은
    // 보류 목록에 그대로 남아, 나중에 설정을 바꾸면 그때 풀린다.
    if (allowedHosts !== null && !isBlocked(h.url, allowedHosts)) {
      keep.push(h);
      continue;
    }
    if (!h.el.isConnected) continue;
    if (h.el.tagName === 'SCRIPT') {
      const fresh = document.createElement('script');
      for (const a of Array.from(h.el.attributes)) {
        // 표식 속성은 옮기지 않는다 — 옮기면 다음 번에 또 보류 대상이 된다.
        if (a.name === 'data-ray-blocked' || a.name.startsWith('data-ray-')) continue;
        // `type="text/plain"` 을 그대로 복사하면 **되살려도 실행되지 않는다.**
        if (a.name === 'type' && a.value === 'text/plain') continue;
        fresh.setAttribute(a.name, a.value);
      }
      fresh.setAttribute(h.attr, h.url);
      h.el.replaceWith(fresh);
    } else {
      h.el.setAttribute(h.attr, h.url);
      h.el.removeAttribute('data-ray-blocked');
    }
    n++;
  }
  held.push(...keep);
  return n;
}

/** 지금 보류 중인 것의 수. 진단용이다. */
export function heldCount(): number {
  return held.length;
}

/**
 * 차단을 켠다. **다른 어떤 코드보다 먼저 불러야 한다.**
 *
 * 되돌리는 함수를 돌려준다 — 테스트에서 전역 패치를 걷어내는 데 쓴다.
 */
export function startBlocking(config: BannerConfig): () => void {
  const hosts = allBlockHosts(config);
  if (hosts.length === 0) {
    // 차단 대상 선언이 없어도 **표식은 처리한다.** 사이트가 표식만 달고
    // 목록을 안 줬다면, 그 표식이 영원히 안 풀리는 쪽이 더 나쁘다.
    watchMarked();
    return () => undefined;
  }
  const restore = interceptFuture(hosts);
  holdExisting(hosts);
  watchMarked();
  return restore;
}
